import { useEffect, useState } from 'react';
import { fetchDeclarations, submitPayment } from '../services/api';

export default function PaymentsPage() {
  const [declarations, setDeclarations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  // Form state
  const [selectedDecId, setSelectedDecId] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [channel, setChannel] = useState<string>('BCC_TRANSFER');
  const [phone, setPhone] = useState('');

  const [paymentInfo, setPaymentInfo] = useState<{ reference?: string; instructions?: string } | null>(null);

  useEffect(() => {
    fetchDeclarations()
      .then(decs => {
        const arr = Array.isArray(decs) ? decs : [];
        setDeclarations(arr);
        const payable = arr.filter(d => d.status === 'VALIDATED' || d.status === 'DECLARED' || d.status === 'AUTO_GENERATED' || d.status === 'SUBMITTED');
        if (payable.length > 0) setSelectedDecId(payable[0].id);
        else if (arr.length > 0) setSelectedDecId(arr[0].id);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const selectedDec = declarations.find(d => d.id === selectedDecId);
  const isPayable = selectedDec && (selectedDec.status === 'VALIDATED' || selectedDec.status === 'DECLARED' || selectedDec.status === 'AUTO_GENERATED' || selectedDec.status === 'SUBMITTED');
  const exchangeRate = selectedDec?.exchange_rate || 2750;
  const dueUsd = selectedDec ? Math.round(((selectedDec.collected_usd || selectedDec.system_amount || selectedDec.amount_usd || 0) * (selectedDec.fpt_rate || 0.04)) * 100) / 100 : 0;
  const dueCdf = selectedDec ? (selectedDec.collected_cdf ? Math.round(selectedDec.collected_cdf * (selectedDec.fpt_rate || 0.04)) : Math.round(dueUsd * exchangeRate)) : 0;

  const handlePay = async () => {
    if (!selectedDecId) { setError('Sélectionnez une déclaration'); return; }
    const amt = parseFloat(amount);
    const finalAmount = !isNaN(amt) && amt > 0 ? amt : (currency === 'CDF' ? dueCdf : dueUsd);

    if (finalAmount <= 0) {
      setError('Le montant doit être supérieur à zéro');
      return;
    }

    setSubmitting(true);
    setMsg('');
    setError('');
    setPaymentInfo(null);
    try {
      const res = await submitPayment({
        declaration_id: selectedDecId,
        channel,
        amount: finalAmount,
        currency,
        idempotency_key: `pay-${selectedDecId}-${Date.now()}`,
        phone_number: channel.includes('MONEY') || channel === 'MPESA' ? phone : undefined,
      });
      setMsg('Paiement initié avec succès');
      if (res?.payment_reference || res?.instructions) {
        setPaymentInfo({
          reference: res.payment_reference || res?.payment?.payment_reference,
          instructions: res.instructions,
        });
      }
      setAmount('');
      setPhone('');
      fetchDeclarations().then(d => setDeclarations(Array.isArray(d) ? d : [])).catch(() => {});
    } catch (e: any) {
      setError(e.message || 'Erreur lors du paiement');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading">Chargement...</div>;

  return (
    <div className="card">
      <h3>Paiements FPT</h3>

      <div className="grid2" style={{ marginTop: 18 }}>
        <div className="info"><small>FPT dû (USD)</small><strong>{dueUsd.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} USD</strong></div>
        <div className="info"><small>Équivalent CDF</small><strong>{dueCdf.toLocaleString('fr-FR')} CDF</strong></div>
      </div>

      {error && <div className="error-msg">{error}</div>}
      {msg && <div className="notice" style={{ marginTop: 12, background: '#eaf8f1', borderColor: '#a3dfc4', color: 'var(--green)' }}>{msg}</div>}

      {paymentInfo && (
        <div className="notice" style={{ marginTop: 12, background: '#f0f9ff', borderColor: '#bae6fd', color: '#0369a1' }}>
          {paymentInfo.reference && <p><strong>Référence de paiement :</strong> {paymentInfo.reference}</p>}
          {paymentInfo.instructions && (
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.875rem', marginTop: 8 }}>
              {paymentInfo.instructions}
            </pre>
          )}
        </div>
      )}

      <div style={{ marginTop: 18, display: 'grid', gap: 12 }}>
        <label>
          <small>Déclaration</small>
          <select value={selectedDecId} onChange={e => setSelectedDecId(e.target.value)} style={{ width: '100%', padding: 8 }}>
            {declarations.map(d => {
              const payable = d.status === 'VALIDATED' || d.status === 'DECLARED' || d.status === 'AUTO_GENERATED' || d.status === 'SUBMITTED';
              return (
                <option key={d.id} value={d.id}>
                  {d.declaration_reference || d.period} — {d.hotel_name || ''} [{d.status}]{payable ? '' : ' (non payable)'}
                </option>
              );
            })}
          </select>
        </label>

        <label>
          <small>Canal de paiement</small>
          <select value={channel} onChange={e => setChannel(e.target.value)} style={{ width: '100%', padding: 8 }}>
            <option value="BCC_TRANSFER">Virement bancaire (BCC)</option>
            <option value="MPESA">M-Pesa</option>
            <option value="ORANGE_MONEY">Orange Money</option>
            <option value="AIRTEL_MONEY">Airtel Money</option>
          </select>
        </label>

        {(channel === 'MPESA' || channel === 'ORANGE_MONEY' || channel === 'AIRTEL_MONEY') && (
          <label>
            <small>Numéro de téléphone</small>
            <input type="tel" placeholder="+243..." value={phone} onChange={e => setPhone(e.target.value)} style={{ width: '100%', padding: 8 }} />
          </label>
        )}

        <div className="grid2">
          <label>
            <small>Montant (vide = montant FPT dû : {currency === 'CDF' ? `${dueCdf.toLocaleString('fr-FR')} CDF` : `${dueUsd.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} USD`})</small>
            <input
              type="number"
              step="any"
              placeholder={currency === 'CDF' ? `${dueCdf}` : `${dueUsd}`}
              value={amount}
              onChange={e => setAmount(e.target.value)}
              style={{ width: '100%', padding: 8 }}
            />
          </label>
          <label>
            <small>Devise</small>
            <select value={currency} onChange={e => setCurrency(e.target.value)} style={{ width: '100%', padding: 8 }}>
              <option value="USD">USD</option>
              <option value="CDF">CDF</option>
            </select>
          </label>
        </div>
      </div>

      {!isPayable && selectedDec && (
        <div className="notice" style={{ marginTop: 12, background: '#fff3cd', borderColor: '#ffc107', color: '#856404' }}>
          Cette déclaration ne peut pas être payée dans son état actuel. Statut : {selectedDec.status}
        </div>
      )}

      <div className="actions">
        <button className="btn primary" disabled={submitting || !isPayable} onClick={handlePay}>
          {submitting ? 'Traitement...' : 'Soumettre le paiement'}
        </button>
      </div>
    </div>
  );
}
